import React, { useState } from 'react';
import axiosClient from '../../lib/auth';
import { decodeToken } from '../../lib/tokenReset';
import { useNavigate, useParams } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Loader2 } from 'lucide-react';

type ResetPasswordPageParams = {
  rawToken: string;
};

interface CreatePasswordProps {
  userFlow: 'reset' | 'welcome';
}

export default function CreatePassword({ userFlow }: CreatePasswordProps) {
  const { rawToken } = useParams<ResetPasswordPageParams>();

  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [resetError, setResetError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setResetError('');

    if (!value) {
      setPasswordError('Password is required');
    } else if (value.length < 8) {
      setPasswordError('Password must be at least 8 characters');
    } else if (value.length > 128) {
      setPasswordError('Password must be no more than 128 characters');
    } else {
      setPasswordError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Decode the raw token
    const decodedToken = decodeToken(rawToken);
    if (decodedToken === null) {
      setResetError('Invalid link token, please ensure to copy the entire URL from the email.');
      return;
    }

    if (!password) {
      setPasswordError('Password is required');
      return;
    }

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (password.length > 128) {
      setPasswordError('Password must be no more than 128 characters');
      return;
    }

    setIsLoading(true);
    setResetError('');

    try {
      const response = await axiosClient.post(`/Auth/DoTokenReset`, {
        password: password.trim(),
        token: decodedToken.token,
        userId: decodedToken.userId,
      });

      if (response.data.message) {
        setPassword('');
        // setResetPasswordResponse(response.data.message);
        setShowSuccessDialog(true);
      } else {
        setResetError('Failed to set new password. Please try again.');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Reset password failed:', error);
      setResetError(
        error.response?.data?.message || 'Failed to set new password. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="flex justify-between gap-4 border-b border-[#2E3835] text-white">
        <img src="/logo.svg" className="w-32 border-r p-4" />
        <a className="text-lime border-r p-4" href="/">
          Back to Login
        </a>
      </div>
      <div className="mx-auto mt-[15vh] w-full max-w-[40rem] rounded-md border border-[#2E3835] bg-[#222] shadow-md">
        <div className="border-b border-[#2E3835] p-6">
          <img src="/logo.svg" className="w-32" />
        </div>
        <div className="flex flex-col gap-4 p-4">
          <h1 className="border-b border-[#2E3835] pb-4 text-xl font-bold text-white">
            {userFlow == 'reset' ? 'Reset Password' : 'Welcome to Watchful!'}
          </h1>

          <form onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="password">Create a new password</Label>
              <Input
                id="password"
                type="password"
                className="mt-2 w-full border-[#2E3835] bg-inherit p-2 text-white shadow-md focus:border-green-500 focus:outline-none"
                placeholder="Password"
                value={password}
                onChange={handlePasswordChange}
                required
                disabled={isLoading}
                autoFocus
              />
              {passwordError && <p className="mt-1 text-sm text-red-500">{passwordError}</p>}
            </div>
            {resetError && (
              <div className="my-4 rounded-md border border-red-500/20 bg-red-500/10 p-3">
                <p className="text-sm text-red-500">{resetError}</p>
              </div>
            )}

            <Button
              type="submit"
              className="bg-lime mt-4 w-full text-black"
              disabled={isLoading || !password}
            >
              {isLoading ? (
                <>
                  Setting Password...
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                </>
              ) : userFlow == 'reset' ? (
                'Reset Password'
              ) : (
                'Create new password'
              )}
            </Button>
          </form>

          <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {userFlow == 'reset' ? 'Password Reset' : 'Account Creation'} Successful
                </DialogTitle>
                <DialogDescription>
                  Your password has been successfully set. You can now log in with your new
                  password.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => navigate('/login')}>Go to Login</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  );
}
